import { useEffect, useMemo, useRef, useState } from 'react'
import { registerComponent } from 'react-register-dom'
import Accordion from '@components/UI/Accordion'
import Toast from '@components/UI/Toast'
import { cls } from '@utils/classnames'
import { isMq1, useWindowResize } from '@utils/mediaQueries'

type SocialIcon = 'facebook' | 'x' | 'linkedin' | 'whatsapp' | 'copy'

const socialIconDisplayNames: Record<SocialIcon, string> = {
  facebook: 'Facebook',
  x: 'X',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  copy: '',
}

export interface ShareLinksProperties {
  id: string
  leadText?: string
  shareButtonAriaLabel?: string
  closeShareButtonAriaLabel?: string
  copyButtonAriaLabel?: string
  enabledSocialIcons: SocialIcon[]
  pagePublishUrl: string
  copiedToClipboardText: string
  shareLinkRelAttribute?: string
}

const socialIconToIconClassNameMapping: Record<SocialIcon, string> = {
  facebook: 'ri-facebook-circle-line',
  copy: 'ri-file-copy-line',
  linkedin: 'ri-linkedin-box-line',
  x: 'ri-twitter-x-line',
  whatsapp: 'ri-whatsapp-line',
}

const getLinkForSocialShareByIcon: Record<SocialIcon, (publishUrl: string) => string> = {
  facebook: (publishUrl: string) => {
    return `http://www.facebook.com/share.php?${encodeURIComponent(publishUrl)}`
  },
  copy: (publishUrl: string) => publishUrl,
  linkedin: (publishUrl: string) => {
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publishUrl)}`
  },
  x: (publishUrl: string) => {
    return `https://x.com/intent/tweet?text=${encodeURIComponent(publishUrl)}`
  },
  whatsapp: (publishUrl: string) => `https://wa.me?${encodeURIComponent(publishUrl)}`,
}

function pushShareEvent(platform: SocialIcon, url: string) {
  if (window.adobeDataLayer) {
    window.adobeDataLayer.push({
      event: 'clicked share',
      eventInfo: {
        reference: 'share',
        platform: platform,
        requestUrl: url,
      },
    })
  }
}

const ShareLinks: React.FC<{
  jsonData: string
}> = ({ jsonData }) => {
  const [notificationVisible, setNotificationVisible] = useState(false)
  const config = useMemo(() => JSON.parse(jsonData) as ShareLinksProperties, [jsonData])
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const forceAccordionOpen = !useWindowResize(isMq1)
  const srAnnouncerReference = useRef<HTMLDivElement>(null)

  // Handle Escape key to close notification
  useEffect(() => {
    if (!notificationVisible) return

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismissNotification()
      }
    }

    window.addEventListener('keydown', handleEscapeKey)
    return () => window.removeEventListener('keydown', handleEscapeKey)
  }, [notificationVisible])

  function toggleAccordion() {
    setIsAccordionOpen((open) => !open)
  }

  function copyShareLinkToClipboard() {
    navigator.clipboard.writeText(config.pagePublishUrl)
    pushShareEvent('copy', config.pagePublishUrl)
    setNotificationVisible(true)

    // Trigger screen reader announcement
    if (srAnnouncerReference.current) {
      srAnnouncerReference.current.textContent = ''
      setTimeout(() => {
        if (srAnnouncerReference.current) {
          srAnnouncerReference.current.textContent = config.copiedToClipboardText
        }
      }, 50)
    }
  }

  function dismissNotification() {
    setNotificationVisible(false)
  }

  return (
    <div className="cmp-share-links">
      {/* Hidden screen reader announcer for copy notification */}
      <div
        id={`sr-share-announcer-${config.id}`}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        ref={srAnnouncerReference}
      />

      <p className="cmp-share-links__lead-text">{(forceAccordionOpen || !isAccordionOpen) && config.leadText}</p>
      <div className="cmp-share-links__icons-section">
        <Accordion isOpen={isAccordionOpen || forceAccordionOpen} horizontal={true}>
          <div className="cmp-share-links__icon-list">
            {config.enabledSocialIcons.map((icon) =>
              icon === 'copy' ? (
                <button
                  className="cmp-share-links__open-icon-list-icon-button"
                  onClick={copyShareLinkToClipboard}
                  key={icon}
                  aria-label={config.copyButtonAriaLabel || 'Copy to clipboard'}
                >
                  <i className={socialIconToIconClassNameMapping[icon]} />
                  <Toast
                    isVisible={notificationVisible}
                    onClose={dismissNotification}
                    type="success"
                    closeButtonAriaLabel={config.closeShareButtonAriaLabel}
                  >
                    {config.copiedToClipboardText}
                  </Toast>
                </button>
              ) : (
                <a
                  className="cmp-share-links__icon-link"
                  key={icon}
                  target="_blank"
                  onClick={() => pushShareEvent(icon, getLinkForSocialShareByIcon[icon](config.pagePublishUrl))}
                  href={getLinkForSocialShareByIcon[icon](config.pagePublishUrl)}
                  rel={config.shareLinkRelAttribute || ''}
                  aria-label={`${config.leadText} ${socialIconDisplayNames[icon]}`}
                >
                  <i className={socialIconToIconClassNameMapping[icon]} />
                </a>
              ),
            )}
          </div>
        </Accordion>
        <button
          className="cmp-share-links__open-icon-list-button"
          onClick={toggleAccordion}
          aria-label={isAccordionOpen ? config.closeShareButtonAriaLabel : config.shareButtonAriaLabel}
        >
          <i
            className={cls({
              'ri-share-line': !isAccordionOpen,
              'ri-close-line': isAccordionOpen,
            })}
          />
        </button>
      </div>
    </div>
  )
}

registerComponent('ShareLinks', ShareLinks)

export default ShareLinks
